export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const billTypeList = [
  { id: 1, name: "Abstract" },
  { id: 2, name: "Mineral" },
  { id: 3, name: "Tract" },
  { id: 4, name: "Cursory Title" },
  { id: 5, name: "Due Dilligence" },
  { id: 6, name: "Area Checks" },
  { id: 7, name: "Curative" },
  { id: 8, name: "Other" },
];

export const DAILY_HOUR_LIMIT = 8;

export const createEmptyLineItem = (date) => ({
  billingType: "",
  dayRate: "0.00",
  hours: "0.00",
  miles: "0",
  mileageRate: "0.00",
  lodging: "0.00",
  copies: "0.00",
  description: "",
  perDiemRate: "0.00",
  perDiem: "0.00",
  misc: "0.00",
  date,
  billingStatementDetailsId: 0,
  isOfficePerDiem: false,
  isTravelPerDiem: false,
});

const pad = (value) => String(value).padStart(2, "0");

export const formatDate = (date) => {
  const source = date instanceof Date ? date : new Date(date);
  return `${pad(source.getMonth() + 1)}/${pad(source.getDate())}/${source.getFullYear()}`;
};

export const formatDisplayDate = (date) => {
  const source = date instanceof Date ? date : new Date(date);
  return `${monthNames[source.getMonth()]} ${source.getDate()} ${source.getFullYear()}`;
};

export const toPeriodKey = (startDate, endDate) =>
  `${formatDate(startDate).replaceAll("/", "")}${formatDate(endDate).replaceAll("/", "")}`;

export const getBillingPeriodForDate = (date, billingPeriodDays = 15) => {
  const source = date instanceof Date ? date : new Date(date);
  const year = source.getFullYear();
  const month = source.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const periodDays =
    Number(billingPeriodDays) > 0 ? Number(billingPeriodDays) : 15;
  let startDay = 1;
  let resolvedStartDay = 1;
  let resolvedEndDay = daysInMonth;

  while (startDay <= daysInMonth) {
    let endDay = startDay + periodDays - 1;

    if (endDay > daysInMonth) {
      endDay = daysInMonth;
    }

    if (endDay + periodDays > daysInMonth) {
      endDay = daysInMonth;
    }

    if (source.getDate() >= startDay && source.getDate() <= endDay) {
      resolvedStartDay = startDay;
      resolvedEndDay = endDay;
      break;
    }

    startDay = endDay + 1;
  }

  const start = new Date(year, month, resolvedStartDay);
  const end = new Date(year, month, resolvedEndDay);

  return {
    startDate: start,
    endDate: end,
    billingStart: formatDate(start),
    billingEnd: formatDate(end),
    periodKey: toPeriodKey(start, end),
  };
};

export const getCyclePeriodForDate = (date) => {
  const source = date instanceof Date ? new Date(date) : new Date(date);
  const year = source.getFullYear();
  const month = source.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = source.getDate() <= 14 ? 1 : 15;
  const endDay = source.getDate() <= 14 ? 14 : daysInMonth;
  const startDate = new Date(year, month, startDay);
  const endDate = new Date(year, month, endDay);

  return {
    startDate,
    endDate,
    billingStart: formatDate(startDate),
    billingEnd: formatDate(endDate),
    periodKey: toPeriodKey(startDate, endDate),
  };
};

export const getAdjacentCycleDate = (date, direction) => {
  const source = date instanceof Date ? new Date(date) : new Date(date);
  const cycle = getCyclePeriodForDate(source);
  const step = direction < 0 ? -1 : 1;
  const isFirstCycle = source.getDate() <= 14;

  if (step < 0 && isFirstCycle) {
    return new Date(source.getFullYear(), source.getMonth(), 0);
  }

  if (step > 0 && !isFirstCycle) {
    return new Date(source.getFullYear(), source.getMonth() + 1, 1);
  }

  return new Date(
    cycle.startDate.getFullYear(),
    cycle.startDate.getMonth(),
    isFirstCycle ? 15 : 1,
  );
};

export const parseNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseBoolean = (value) => {
  if (value === true || value === false) return value;
  if (value == null) return false;

  if (typeof value === "number") return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (normalized === "true" || normalized === "1" || normalized === "yes") return true;
  if (normalized === "false" || normalized === "0" || normalized === "no") return false;

  return Boolean(value);
};

export const calculatePerDiem = (hours, rate) => {
  const parsedRate = parseNumber(rate);
  const parsedHours = Math.min(parseNumber(hours), DAILY_HOUR_LIMIT);
  return ((parsedHours / DAILY_HOUR_LIMIT) * parsedRate).toFixed(2);
};

export const calculateLineTotal = (item) => {
  const hours = parseNumber(item.hours);
  const dayRate = parseNumber(item.dayRate);
  const miles = parseNumber(item.miles);
  const mileageRate = parseNumber(item.mileageRate);
  const lodging = parseNumber(item.lodging);
  const copies = parseNumber(item.copies);
  const misc = parseNumber(item.misc);
  const perDiem = parseNumber(calculatePerDiem(item.hours, item.perDiemRate));
  const charges = dayRate
    ? (Math.min(hours, DAILY_HOUR_LIMIT) / DAILY_HOUR_LIMIT) * dayRate
    : 0;

  return charges + miles * mileageRate + lodging + copies + misc + perDiem;
};

export const calculateTotals = (items = []) =>
  items.reduce(
    (acc, item) => {
      acc.hours += parseNumber(item.hours);
      acc.amount += calculateLineTotal(item);
      return acc;
    },
    { hours: 0, amount: 0 }
  );

export const formatHoursSummary = (value) => `${parseNumber(value).toFixed(2)} Hrs`;

export const formatCurrency = (value) => `$${parseNumber(value).toFixed(2)}`;

export const buildCalendarDays = (
  year,
  monthIndex,
  selectedDay,
  metaByDate,
  activePeriod = null,
) => {
  const firstDay = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const mondayStartIndex = (firstDay.getDay() + 6) % 7;

  return Array.from({ length: 42 }).map((_, index) => {
    const dayOffset = index - mondayStartIndex + 1;
    const date = new Date(year, monthIndex, dayOffset);
    const dateKey = formatDate(date);
    const dayNumber = date.getDate();
    const isCurrentMonth = date.getMonth() === monthIndex;
    const isInActivePeriod = activePeriod
      ? date >= activePeriod.startDate && date <= activePeriod.endDate
      : true;

    return {
      date,
      dateKey,
      dayNumber,
      isCurrentMonth,
      isSelected: isCurrentMonth && dayNumber === selectedDay,
      isCycleOutside: Boolean(activePeriod) && !isInActivePeriod,
      // Cycle-outside dates remain disabled, but their existing entries should
      // still be visible in the calendar.
      meta: (isCurrentMonth || (activePeriod && isInActivePeriod))
        ? metaByDate[dateKey]
        : null,
    };
  });
};

export const buildWeekDays = (selectedDate, metaByDate) => {
  const source = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
  const weekday = source.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;

  return weekDays.slice(0, 7).map((dayLabel, index) => {
    const date = new Date(source);
    date.setDate(source.getDate() + mondayOffset + index);
    const dateKey = formatDate(date);

    return {
      date,
      dateKey,
      label: `${dayLabel.slice(0, 3)} ${pad(date.getMonth() + 1)}/${pad(date.getDate())}`,
      meta: metaByDate[dateKey] || null,
    };
  });
};

export const buildDayMeta = (items = []) => {
  const totals = calculateTotals(items);

  return {
    amount: formatCurrency(totals.amount),
    hrs: formatHoursSummary(totals.hours),
    count: String(items.filter((item) => item.billingType).length).padStart(2, "0"),
    alert: totals.hours > DAILY_HOUR_LIMIT,
  };
};

export const getWeekRange = (selectedDate) => {
  const source = selectedDate instanceof Date ? new Date(selectedDate) : new Date(selectedDate);
  const weekday = source.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  const startDate = new Date(source);
  startDate.setDate(source.getDate() + mondayOffset);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  return {
    startDate,
    endDate,
    startDateString: formatDate(startDate),
    endDateString: formatDate(endDate),
  };
};

const resolveRecordDate = (record) => {
  const rawValue =
    record?.Date ||
    record?.date ||
    record?.BillingDate ||
    record?.billingDate ||
    record?.entryDate ||
    record?.EntryDate ||
    "";

  if (!rawValue) return "";

  const normalized = String(rawValue).trim();
  const isoDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return formatDate(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const parsedDate = new Date(rawValue);
  return Number.isNaN(parsedDate.getTime()) ? rawValue : formatDate(parsedDate);
};

const resolveRecordCount = (record) =>
  parseNumber(
    record?.ProjectCount ??
      record?.projectCount ??
      record?.count ??
      record?.Count ??
      record?.itemCount ??
      record?.ItemCount ??
      record?.detailsCount ??
      record?.DetailsCount ??
      1
  );

export const buildDayMetaFromApiRecords = (records = []) => {
  const grouped = records.reduce((acc, record) => {
    const dateKey = resolveRecordDate(record);
    if (!dateKey) return acc;

    if (!acc[dateKey]) {
      acc[dateKey] = {
        amount: 0,
        hours: 0,
        count: 0,
      };
    }

    acc[dateKey].hours += parseNumber(
      record?.TotalHours ?? record?.totalHours ?? record?.Hours ?? record?.hours
    );
    acc[dateKey].amount += parseNumber(
      record?.TotalAmount ?? record?.totalAmount ?? record?.Amount ?? record?.amount
    );
    acc[dateKey].count += resolveRecordCount(record);

    return acc;
  }, {});

  return Object.entries(grouped).reduce((acc, [dateKey, value]) => {
    acc[dateKey] = {
      amount: formatCurrency(value.amount),
      hrs: formatHoursSummary(value.hours),
      count: String(value.count).padStart(2, "0"),
      alert: value.hours > DAILY_HOUR_LIMIT,
    };
    return acc;
  }, {});
};

export const mapApiDetailToItem = (detail) => ({
  billingType: detail.BillingType ? String(detail.BillingType) : "",
  dayRate: parseNumber(detail.DayRate).toFixed(2),
  hours: parseNumber(detail.Hours).toFixed(2),
  miles: parseNumber(detail.Miles).toString(),
  mileageRate: parseNumber(detail.mileageRate).toFixed(2),
  lodging: parseNumber(detail.Lodging).toFixed(2),
  copies: parseNumber(detail.Copies).toFixed(2),
  description: detail.Description || "",
  perDiemRate: parseNumber(detail.PerDiemRate).toFixed(2),
  perDiem: parseNumber(detail.PerDiem).toFixed(2),
  misc: parseNumber(detail.Misc).toFixed(2),
  date: detail.Date,
  billingStatementDetailsId: detail.billingStatementDetailsId || 0,
  isOfficePerDiem: parseBoolean(detail.IsOfficePerDiem),
  isTravelPerDiem: parseBoolean(detail.IsTravelPerDiem),
});
